/**
 * Reading the blobs that would not fit inside a gift wrap.
 *
 * A gift wrap encrypts twice, leaving about 40KB of usable body, and 36% of
 * real mail exceeds that. Those bodies and every attachment are sealed under
 * their own AES-256-GCM key, stored by the SHA-256 of the *ciphertext*, and
 * only the key travels inside the sealed event. The store therefore holds
 * nothing it can read, and this is the other half of `internal/blob` in Go.
 *
 * Three details are worth stating because each one fails silently:
 *
 * 1. The nonce is the first 12 bytes of the ciphertext. WebCrypto wants it as a
 *    separate `iv`; handing it the whole buffer fails with an unhelpful
 *    "operation failed" that looks like a wrong key.
 * 2. The digest is of the ciphertext, not the plaintext, and it is checked
 *    before decrypting. The store is addressed by content, so this is what
 *    makes a substituted blob a failure rather than a garbled message.
 * 3. Every request carries a fresh signed authorization naming that one blob.
 *    With a phone signer that is one approval per fetch, which is why the
 *    plaintext is cached by the caller and never fetched twice.
 */
import type { BlobRef } from "./seal";
import type { MailSigner } from "./signer";

/** BUD-01's authorization kind. Must match internal/blossom. */
export const KIND_BLOSSOM_AUTH = 24242;

export const BLOSSOM_URL: string =
  import.meta.env["VITE_BLOSSOM_URL"] ?? "https://blossom.nicemail.ca";

/** How long an authorization is good for. Short: it names one blob and one verb. */
const AUTH_TTL_SECONDS = 600;

export interface AuthDraft {
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
}

/** The kind 24242 event `blossom.Verify` demands, as an Authorization header. */
export async function authorization(
  signer: MailSigner,
  verb: "get" | "upload",
  hash: string,
  nowMs = Date.now(),
): Promise<string> {
  const now = Math.floor(nowMs / 1000);
  const event = await signer.signEvent({
    kind: KIND_BLOSSOM_AUTH,
    created_at: now,
    tags: [
      ["t", verb],
      ["x", hash],
      ["expiration", String(now + AUTH_TTL_SECONDS)],
    ],
    content: `${verb} blob`,
  });
  return `Nostr ${base64(new TextEncoder().encode(JSON.stringify(event)))}`;
}

/** Fetch one blob and open it. Throws with a readable reason on every failure. */
export async function fetchBlob(
  ref: BlobRef,
  signer: MailSigner,
  baseUrl = BLOSSOM_URL,
): Promise<Uint8Array> {
  const auth = await authorization(signer, "get", ref.sha256);
  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/$/, "")}/${ref.sha256}`, {
      headers: { authorization: auth },
    });
  } catch {
    // `fetch` rejects with a bare TypeError for a refused connection and for a
    // failed CORS preflight alike, and the browser keeps the reason to itself.
    // The preflight is by far the likelier of the two — the authorization
    // header forces one on every request — so say so rather than "failed".
    throw new Error(
      `${baseUrl} could not be reached. If it is up, it is most likely refusing ` +
      `the browser's preflight: the store must answer OPTIONS with CORS headers.`,
    );
  }
  if (!response.ok) {
    // The store explains itself in the body — "pubkey not allowed" is the one
    // worth reading, and it means this reader is not on POSTR_BLOSSOM_ALLOWED.
    const reason = (await response.text().catch(() => "")).trim().slice(0, 120);
    throw new Error(`the blob store answered ${response.status}${reason ? `: ${reason}` : ""}`);
  }
  const ciphertext = new Uint8Array(await response.arrayBuffer());
  await verifyDigest(ciphertext, ref.sha256);
  return decryptBlob(ref.key, ciphertext);
}

/** The store is content-addressed, so bytes that do not match are not the blob. */
export async function verifyDigest(ciphertext: Uint8Array, expected: string): Promise<void> {
  const digest = await crypto.subtle.digest("SHA-256", toBuffer(ciphertext));
  const got = hex(new Uint8Array(digest));
  if (got !== expected.toLowerCase()) {
    throw new Error(`blob digest mismatch: got ${got.slice(0, 12)}…`);
  }
}

/** AES-256-GCM with the nonce prefixed, which is what `internal/blob` writes. */
export async function decryptBlob(keyHex: string, ciphertext: Uint8Array): Promise<Uint8Array> {
  const raw = fromHex(keyHex);
  if (raw.length !== 32) throw new Error(`key is ${raw.length} bytes, want 32`);
  const NONCE = 12;
  if (ciphertext.length < NONCE) throw new Error("ciphertext shorter than nonce");

  const key = await crypto.subtle.importKey("raw", toBuffer(raw), "AES-GCM", false, ["decrypt"]);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toBuffer(ciphertext.subarray(0, NONCE)), tagLength: 128 },
    key,
    toBuffer(ciphertext.subarray(NONCE)),
  );
  return new Uint8Array(plaintext);
}

/** A copy, because a subarray's buffer is the whole blob and WebCrypto takes buffers. */
function toBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.slice().buffer;
}

function fromHex(value: string): Uint8Array {
  const clean = value.trim().toLowerCase();
  if (!/^([0-9a-f]{2})*$/.test(clean)) throw new Error("key is not hex");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}
